import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  classroomContextId,
  withClassroomContext,
} from "@/components/workspace/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { useViewport } from "@/hooks/useViewport";
import { IoCloudUploadOutline, IoExitOutline } from "react-icons/io5";
import UnifiedActionButton from "@/features/tutor/components/buttons/UnifiedActionButton";
import { formatLastSaved } from "../spacesTimeUtils";
import "../space-live.css";

// ─── Types ───────────────────────────────────────────────────────────────────

export type SpaceBottomBarProps = {
  spaceName: string;
  teacherName: string | null;
  voiceEnabled?: boolean;
  isWsOpen?: boolean;
  isRecording?: boolean;
  onMicDown?: () => void;
  onMicUp?: () => void;
  spaceClosed?: boolean;
  onSave?: () => Promise<void>;
  lastSaved?: Date | null;
  agentPanelEnabled?: boolean;
  isAgentPanelOpen?: boolean;
  onToggleAgentPanel?: () => void;
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

function useSave(onSave?: () => Promise<void>) {
  const [isSaving, setIsSaving] = useState(false);
  async function handleSave() {
    if (!onSave || isSaving) return;
    setIsSaving(true);
    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  }
  return { isSaving, handleSave };
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function ChatButton({
  isOpen,
  onToggle,
  size = 48,
}: {
  isOpen: boolean;
  onToggle: () => void;
  size?: number;
}) {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center justify-center shrink-0 transition-colors focus:outline-none rounded-[6px] ${
        isOpen ? "et-space-primary" : "et-space-chip"
      }`}
      style={{ width: size, height: size }}
      aria-label={isOpen ? "Close chat" : "Open chat"}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M20 2H4C2.9 2 2 2.9 2 4v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"
          fill={isOpen ? "#2C2C3A" : "white"}
        />
      </svg>
    </button>
  );
}

function SaveButton({
  isSaving,
  lastSaved,
  onSave,
}: {
  isSaving: boolean;
  lastSaved?: Date | null;
  onSave: () => void;
}) {
  return (
    <button
      onClick={onSave}
      disabled={isSaving}
      title="Save now"
      className="flex flex-col items-center gap-0.5 text-white/50 hover:text-white/80 transition-colors disabled:opacity-40"
    >
      <IoCloudUploadOutline
        className={`w-5 h-5 ${isSaving ? "animate-pulse" : ""}`}
      />
      <span className="text-[10px] leading-none">
        {isSaving ? "Saving…" : formatLastSaved(lastSaved)}
      </span>
    </button>
  );
}

function SpaceInfo({
  spaceName,
  teacherName,
}: {
  spaceName: string;
  teacherName: string;
}) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-sm font-semibold text-white/80 truncate">
        {spaceName}
      </span>
      <span className="text-sm text-white/40 shrink-0">·</span>
      <span className="text-sm text-white/50 truncate">{teacherName}</span>
    </div>
  );
}

function LeaveButton({
  compact = false,
  spaceClosed = false,
}: {
  compact?: boolean;
  spaceClosed?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const classroomId = classroomContextId(location.pathname, location.search);
  return (
    <button
      aria-label={spaceClosed ? "Back" : "Leave Space"}
      onClick={() =>
        spaceClosed
          ? navigate(-1)
          : navigate(withClassroomContext("/spaces", classroomId))
      }
      style={compact ? { width: 48, height: 48 } : { borderRadius: "6px" }}
      className={`et-space-danger flex items-center justify-center focus:outline-none transition-colors duration-200 shrink-0 ${compact ? "rounded-[6px]" : "px-4 h-[48px]"}`}
    >
      {compact ? (
        <IoExitOutline className="w-5 h-5" />
      ) : (
        <span style={{ fontWeight: 700, fontSize: "14px" }}>
          {spaceClosed ? "Back" : "Leave Space"}
        </span>
      )}
    </button>
  );
}

// ─── Mobile landscape layout (right sidebar) ────────────────────────────────

function MobileLandscapeBottomBar({
  voiceEnabled,
  isWsOpen,
  isRecording,
  onMicDown,
  onMicUp,
  spaceClosed,
  onSave,
  lastSaved,
  agentPanelEnabled,
  isAgentPanelOpen,
  onToggleAgentPanel,
}: SpaceBottomBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isSaving, handleSave } = useSave(onSave);

  return (
    <div className="flex flex-row items-stretch et-space-frame h-full overflow-hidden">
      {/* Left expand/collapse strip */}
      <button
        onClick={() => setIsExpanded((o) => !o)}
        className="h-full flex items-center justify-center shrink-0"
        style={{ width: "28px" }}
        aria-label={isExpanded ? "Collapse" : "Expand"}
      >
        <svg width="10" height="20" viewBox="0 0 10 20" fill="none">
          <polyline
            points={isExpanded ? "1,2 7,10 1,18" : "9,2 3,10 9,18"}
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {/* Extra column — only visible when expanded */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="landscape-expanded"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 60, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
            className="flex flex-col items-center py-3 gap-3 shrink-0"
          >
            <LeaveButton compact spaceClosed={spaceClosed} />
            {onSave && !spaceClosed && (
              <SaveButton
                isSaving={isSaving}
                lastSaved={lastSaved}
                onSave={handleSave}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always visible column: voice + chat */}
      <div className="flex flex-col items-center py-3 px-2 gap-3 shrink-0">
        {/* Voice button — takes all remaining space */}
        {spaceClosed ? (
          <div className="flex-1" />
        ) : voiceEnabled ? (
          <div className="flex-1 flex items-center justify-center w-full">
            <UnifiedActionButton
              isPressingToTalk={isRecording}
              isAwaitingTutorResponse={false}
              hasPendingWork={false}
              isWsConnected={isWsOpen}
              micUnavailable={!isWsOpen}
              hideWarningIcon
              showKeyboardGuide={false}
              onPointerDown={onMicDown}
              onPointerUp={onMicUp}
              compact
              stretched
            />
          </div>
        ) : (
          <div className="flex-1" />
        )}
        {agentPanelEnabled && (
          <ChatButton
            isOpen={!!isAgentPanelOpen}
            onToggle={() => onToggleAgentPanel?.()}
            size={48}
          />
        )}
      </div>
    </div>
  );
}

// ─── Mobile portrait layout ───────────────────────────────────────────────────

function MobilePortraitBottomBar({
  spaceName,
  teacherName,
  voiceEnabled,
  isWsOpen,
  isRecording,
  onMicDown,
  onMicUp,
  spaceClosed,
  onSave,
  lastSaved,
  agentPanelEnabled,
  isAgentPanelOpen,
  onToggleAgentPanel,
}: SpaceBottomBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isSaving, handleSave } = useSave(onSave);

  return (
    <div className="flex flex-col px-3 pb-6">
      {/* Expand/collapse toggle */}
      <button
        onClick={() => setIsExpanded((o) => !o)}
        className="w-full flex items-center justify-center"
        style={{ height: "32px" }}
        aria-label={isExpanded ? "Collapse" : "Expand"}
      >
        <svg width="20" height="10" viewBox="0 0 20 10" fill="none">
          <polyline
            points={isExpanded ? "2,1 10,7 18,1" : "2,6 10,0 18,6"}
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {/* Expandable: space info + save + leave */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="portrait-expanded"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="flex items-center justify-between gap-3 pb-3">
              {teacherName && (
                <SpaceInfo spaceName={spaceName} teacherName={teacherName} />
              )}
              <div className="flex items-center gap-3 shrink-0 ml-auto">
                {onSave && (
                  <SaveButton
                    isSaving={isSaving}
                    lastSaved={lastSaved}
                    onSave={handleSave}
                  />
                )}
                <LeaveButton spaceClosed={spaceClosed} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always visible: chat toggle + voice */}
      <div className="flex items-center justify-between gap-2 h-[48px] px-2">
        {spaceClosed ? (
          <div className="flex-1 flex items-center justify-center px-3 py-1 rounded-full bg-white/10 text-white/60 text-xs font-semibold">
            View only
          </div>
        ) : voiceEnabled ? (
          <div className="flex-1 flex items-center justify-center">
            <UnifiedActionButton
              isPressingToTalk={isRecording}
              isAwaitingTutorResponse={false}
              hasPendingWork={false}
              isWsConnected={isWsOpen}
              micUnavailable={!isWsOpen}
              hideWarningIcon
              showKeyboardGuide={false}
              onPointerDown={onMicDown}
              onPointerUp={onMicUp}
              compact
              stretched
            />
          </div>
        ) : null}
        {agentPanelEnabled && (
          <ChatButton
            isOpen={!!isAgentPanelOpen}
            onToggle={() => onToggleAgentPanel?.()}
            size={48}
          />
        )}
      </div>
    </div>
  );
}

// ─── Desktop / landscape layout ───────────────────────────────────────────────

function DesktopBottomBar({
  spaceName,
  teacherName,
  voiceEnabled,
  isWsOpen,
  isRecording,
  onMicDown,
  onMicUp,
  spaceClosed,
  onSave,
  lastSaved,
  agentPanelEnabled,
  isAgentPanelOpen,
  onToggleAgentPanel,
}: SpaceBottomBarProps) {
  const { isSaving, handleSave } = useSave(onSave);

  return (
    <div className="shrink-0 et-space-frame relative flex items-center justify-between px-6 py-4">
      {/* Left: chat + save */}
      {spaceClosed ? (
        <div style={{ width: 90 }} />
      ) : (
        <div className="flex items-center gap-3">
          {agentPanelEnabled && (
            <ChatButton
              isOpen={!!isAgentPanelOpen}
              onToggle={() => onToggleAgentPanel?.()}
            />
          )}
          {onSave && (
            <SaveButton
              isSaving={isSaving}
              lastSaved={lastSaved}
              onSave={handleSave}
            />
          )}
        </div>
      )}

      {/* Center: voice button or view-only badge */}
      {spaceClosed ? (
        <div className="absolute left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white/60 text-xs font-semibold">
          View only
        </div>
      ) : voiceEnabled ? (
        <div className="absolute left-1/2 -translate-x-1/2">
          <UnifiedActionButton
            isPressingToTalk={isRecording}
            isAwaitingTutorResponse={false}
            hasPendingWork={false}
            isWsConnected={isWsOpen}
            micUnavailable={!isWsOpen}
            hideWarningIcon
            showKeyboardGuide={false}
            onPointerDown={onMicDown}
            onPointerUp={onMicUp}
          />
        </div>
      ) : null}

      {/* Right: space info + leave */}
      <div className="flex items-center gap-4">
        {teacherName && (
          <SpaceInfo spaceName={spaceName} teacherName={teacherName} />
        )}
        <LeaveButton spaceClosed={spaceClosed} />
      </div>
    </div>
  );
}

// ─── Entry point ──────────────────────────────────────────────────────────────

export default function SpaceBottomBar(props: SpaceBottomBarProps) {
  const { isMobilePortrait, isMobileLandscape } = useViewport();

  if (isMobileLandscape) {
    return (
      <div
        className="shrink-0 et-space-frame h-full"
        style={{ fontFamily: "Mulish, sans-serif" }}
      >
        <MobileLandscapeBottomBar {...props} />
      </div>
    );
  }

  if (isMobilePortrait) {
    return (
      <div
        className="shrink-0 et-space-frame"
        style={{ fontFamily: "Mulish, sans-serif" }}
      >
        <MobilePortraitBottomBar {...props} />
      </div>
    );
  }

  return <DesktopBottomBar {...props} />;
}
